// Route profile: terrain under a multi-leg route and the glide line flown leg by leg.

import { bearing, distance, lerp, type LngLat } from './geo';
import { groundGlide, marginAt, type Wind } from './glide';

export interface ProfileOptions {
  gr: number;
  /** MSL start altitude; null or below ground means "launch from the ground". */
  startAlt: number | null;
  margin: number;
  launchRamp: number;
  wind: Wind;
  /** Sample spacing along each leg, metres (30-90). */
  step: number;
}

export interface ProfilePoint {
  /** Distance from the first point, metres. */
  d: number;
  at: LngLat;
  leg: number;
}

export interface ProfileSample extends ProfilePoint {
  ground: number;
  /** Glide-line altitude (MSL), NaN after an unflyable leg. */
  alt: number;
  /** alt - ground */
  clearance: number;
  /** clearance below the safety margin */
  low: boolean;
}

export interface Leg {
  from: LngLat;
  to: LngLat;
  dist: number;
  heading: number;
  groundGr: number | null;
}

export interface Profile {
  points: LngLat[];
  samples: ProfileSample[];
  legs: Leg[];
  total: number;
  startAlt: number;
  arrivalAlt: number;
  arrivalAgl: number;
  /** Air glide ratio needed to reach the last point with the margin, ignoring terrain between. */
  requiredGr: number | null;
  unflyable: boolean;
  /** [startD, endD] of sections below the safety margin. */
  lowSections: [number, number][];
  /** Arrives with margin and never dips below it. */
  ok: boolean;
}

/** Points every `step` metres along the route, always including the vertices. */
export function samplePoints(points: LngLat[], step: number): ProfilePoint[] {
  const out: ProfilePoint[] = [];
  let d0 = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const len = distance(a, b);
    const n = Math.max(1, Math.ceil(len / step));
    for (let k = 0; k < n; k++) out.push({ d: d0 + (len * k) / n, at: lerp(a, b, k / n), leg: i });
    d0 += len;
  }
  if (points.length) out.push({ d: d0, at: points[points.length - 1], leg: Math.max(0, points.length - 2) });
  return out;
}

/** Pure profile computation from sampled points and their ground elevations. */
export function buildProfile(points: LngLat[], pts: ProfilePoint[], ground: ArrayLike<number>, o: ProfileOptions): Profile {
  const legs: Leg[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const heading = bearing(points[i], points[i + 1]);
    legs.push({
      from: points[i],
      to: points[i + 1],
      dist: distance(points[i], points[i + 1]),
      heading,
      groundGr: groundGlide(o.gr, heading, o.wind),
    });
  }
  const startAlt = Math.max(ground[0] ?? 0, o.startAlt ?? -Infinity);
  const legStart: number[] = [];
  let acc = 0;
  for (const l of legs) {
    legStart.push(acc);
    acc += l.dist;
  }
  // altitude at the start of each leg
  const legAlt: number[] = [];
  let a = startAlt;
  let unflyable = false;
  for (const l of legs) {
    legAlt.push(a);
    if (l.groundGr === null) {
      unflyable = true;
      a = NaN;
    } else a -= l.dist / l.groundGr;
  }
  const samples: ProfileSample[] = pts.map((p, i) => {
    let alt: number;
    if (legs.length === 0) alt = startAlt;
    else {
      const l = legs[p.leg];
      const into = p.d - legStart[p.leg];
      alt = l.groundGr === null ? NaN : legAlt[p.leg] - into / l.groundGr;
    }
    const g = ground[i];
    const clearance = alt - g;
    const low = !(clearance >= marginAt(o.margin, o.launchRamp, p.d) - 1e-6);
    return { ...p, ground: g, alt, clearance, low };
  });
  const lowSections: [number, number][] = [];
  for (let i = 0; i < samples.length; i++) {
    if (!samples[i].low) continue;
    const startD = i > 0 ? samples[i - 1].d : samples[i].d;
    let j = i;
    while (j + 1 < samples.length && samples[j + 1].low) j++;
    lowSections.push([startD, samples[j].d]);
    i = j;
  }
  const last = samples[samples.length - 1];
  const target = (last?.ground ?? 0) + o.margin;
  let requiredGr: number | null = null;
  if (!unflyable && legs.length) {
    // ground GR = gr * Vg / Va, so the drop is linear in 1/gr.
    const airDist = legs.reduce((s, l) => s + (l.dist * o.gr) / (l.groundGr as number), 0);
    const h = startAlt - target;
    requiredGr = h > 0 ? airDist / h : Infinity;
  }
  return {
    points,
    samples,
    legs,
    total: acc,
    startAlt,
    arrivalAlt: last?.alt ?? NaN,
    arrivalAgl: last ? last.alt - last.ground : NaN,
    requiredGr,
    unflyable,
    lowSections,
    ok: !unflyable && lowSections.length === 0,
  };
}

export async function computeProfile(
  points: LngLat[],
  o: ProfileOptions,
  elevations: (pts: LngLat[]) => Promise<Float32Array>,
): Promise<Profile> {
  const pts = samplePoints(points, o.step);
  const ground = await elevations(pts.map((p) => p.at));
  return buildProfile(points, pts, ground, o);
}
